import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import './style';
import { Avatar, Verified } from './components';
import Input from '../../components/input/input';
import Dropdown from '../../components/dropdown/dropdown';
import { useSelector } from 'react-redux';
import { saveProfile } from './api';
import { Button, notify } from '../../components';
import { Plus } from '@phosphor-icons/react';
import { Otp } from '../event-create/components';
import { resendOtp, sendOtp, verifyOtp } from '../event-create/api';
import { generateUniqueId, isDigits } from '../../utils/commonUtils';
import { getCurrentUser } from '../login/api';
import { uploadImage } from '../event-create/api';
import { IMAGE_TAGS } from '../../constants/imageTags';
import { OTP_LENGTH } from '../../constants/common';

const UserProfile = () => {
  const [name, setName] = useState('');
  const [profilePic, setProfilePic] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [errors, setErrors] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showEditWhatsappNo, setShowEditWhatsappNo] = useState(false);
  const [edit, setEdit] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [profileFile, setProfileFile] = useState('');

  const { userData } = useSelector((state) => state.app);
  const uniqIdentifier = useRef(null);

  useEffect(() => {
    setName(userData?.name);
    setEmail(userData?.email);
    if (userData?.isPhoneVerified) setPhone(userData?.mobile?.toString());
    setWhatsappNumber(userData?.whatsappNumber?.toString());
    setProfilePic(userData?.picture);
  }, [userData]);

  const handleEditClick = () => {
    setEdit(!edit);
  };

  const handleSaveClick = async () => {
    if (!name.trim() || !/^[A-Za-z\s]+$/.test(name)) {
      setErrorMessage('Name cannot be empty and must contain only letters.');
      return;
    }
    setErrorMessage('');

    const payload = {
      name,
      mobile: userData?.mobile,
      whatsappNumber: userData?.whatsappNumber,
      picture: profilePic,
    };

    // const formData = new FormData();
    // formData.append('username', name);
    // formData.append('picture', profilePic);
    // formData.append('mobile', phone);

    saveProfile(payload, () => {
      setEdit(false);
      notify('success', 'Profile updated', {
        position: 'bottom-center',
        duration: 2000,
      });
    });
  };

  const handleNameChange = (e) => {
    setName(e.target.value);

  };

  const handleFile = (file) => {
    const callback = (resp) => {
      setProfileFile(resp?.data?.data?.url);
    };
    uploadImage({ image: file, tag: IMAGE_TAGS.USER_PROFILE, fileName: `${userData?._id || generateUniqueId()}_${IMAGE_TAGS.USER_PROFILE}` }).then(callback);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePic(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleProfilePicChange = (e) => {
    handleFile(e.target.files[0]);
  };


  const handleDropdownChange = (optionId) => {
    switch (optionId) {
    case 'edit-photo':
    case 'upload-photo':
      document.getElementById('profile-pic-upload').click();
      break;
    case 'delete-photo':
      setProfilePic(null);
      break;
    default:
      break;
    }
  };

  const dropdownOptions = [
    { id: 'edit-photo', label: 'Edit Photo' },
    { id: 'upload-photo', label: 'Upload Photo' },
    { id: 'delete-photo', label: 'Delete Photo' },
  ];

  const onClickGetOTP = () => {
    if (phone?.length !== 10 || !isDigits(phone)) {
      setErrors({
        ...errors,
        mobileError: 'Please enter a valid mobile number',
      });
      return;
    }
    uniqIdentifier.current = generateUniqueId();
    sendOtp(phone, uniqIdentifier.current, (data) => {
      if (data?.status === 'success') {
        setIsOtpOpen(true);
        setErrors(null);
      } else {
        if ([data?.message].includes('Invalid phone number')) {
          setErrors({
            ...errors,
            mobileError: 'Please enter a valid mobile number',
          });
        } else if (data?.message === 'Phone number already linked') {
          setErrors({
            ...errors,
            mobileError: 'The Phone number is already linked to a verified user',
          });
        }
        notify('error', data?.message || 'failed to send OTP!');
      }
    });
  };

  const handlePrimaryClick = (otp) => {
    if (!errors?.otpError && otp?.length === OTP_LENGTH) {
      verifyOtp(
        phone,
        otp,
        uniqIdentifier.current,
        (data) => {
          if (data?.status === 'success') {
            getCurrentUser();
            setIsOtpOpen(false);
            setErrors(null);
          } else {
            setErrors({ ...errors, otpError: data?.description?.reason || 'Please enter correct OTP' });
          }
        },
      );
    } else {
      setErrors({ ...errors, otpError: 'Please enter correct OTP' });
    }
  };

  const handleSecondaryClick = ({ clear = false }) => {
    if (clear) {
      setPhone('');
    }
    setErrors({ ...errors, mobileError: '' });
    setIsOtpOpen(false);
  };

  const onClickResend = () => {
    resendOtp(uniqIdentifier.current, phone);
  };

  const onCancel = () => {
    setWhatsappNumber(userData?.whatsappNumber);
    setShowEditWhatsappNo(false);
    setErrors({ ...errors, whatsappError: '' });
  };

  const onClickSave = () => {
    if (whatsappNumber?.length !== 10 || !isDigits(whatsappNumber)) {
      setErrors({ ...errors, whatsappError: 'Please enter a valid mobile number' });
    } else {
      const payload = {
        name,
        mobile: userData?.mobile,
        whatsappNumber,
        profile: userData?.profile,
        picture: profileFile,
      };

      saveProfile(payload, () => {
        setShowEditWhatsappNo(false);
        notify('success', 'Update successful', {
          position: 'bottom-center',
          duration: 2000,
        });
      });
    }
  };

  return (
    <div className="user-profile-wrapper w-full h-full p-6 flex flex-col items-start md:w-[50%] md:items-center md:m-auto mt-[100px] md:mt-[100px]">
      <Helmet>
        <title>My Profile</title>
        <meta
          name="title"
          content="Personalize Your Ticket Resale Experience in India | Take My Tickets"
        />
        <meta
          name="description"
          content="Customize your profile on Take My Tickets and enjoy a personalized ticket resale experience for concerts, sports, and more in India."
        />
      </Helmet>
      <div className="user-profile-container w-full md:items-center">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="heading-24 md:heading-36 md:pt-6">Account Info</div>
            <div className="text-14">Verify your personal details.</div>
          </div>
          <div className="flex flex-col gap-9">
            <div className="flex w-full justify-between">
              <div className="heading-16 text-black">Profile</div>
              <div
                className="text-primary label-14 cursor-pointer"
                onClick={edit ? handleSaveClick : handleEditClick}
              >
                {edit ? 'Save' : 'Edit'}
              </div>
            </div>
            <div className="flex items-center gap-6">
              {profilePic ? (
                <img
                  src={profilePic}
                  alt="Profile Picture"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <Avatar name={name} />
              )}
              {edit ? (
                <>
                  <Dropdown
                    options={dropdownOptions}
                    onChange={handleDropdownChange}
                    width="170px"
                    className="cursor-pointer"
                  />
                  <input
                    type="file"
                    id="profile-pic-upload"
                    accept="image/*"
                    onChange={handleProfilePicChange}
                    hidden
                  />
                </>
              ) : (
                <div className="text-14 text-grey-70">{name}</div>
              )}
            </div>
            <div className="flex flex-col gap-3">
              {edit && (
                <>
                  <div className="flex justify-between">
                    <div className="heading-16 text-black">Full name</div>
                  </div>
                  <Input type="text"
                    value={name}
                    onChange={handleNameChange} 
                    width='full'
                  />
                  {errorMessage && (
                    <div className="text-red-500 text-12">{errorMessage}</div>
                  )}
                </>
              )}
            </div>
            {email && (
              <div className="flex flex-col gap-3">
                <Verified heading="Email ID"
                  isVerified={userData?.isEmailVerified}
                  content={email} />
              </div>)}
            {(userData?.mobile && userData?.isPhoneVerified) ? ( 
              <div className="flex gap-7 items-center">
                <Input
                  value={userData?.mobile}
                  disabled={true}
                  required={true}
                  contentLeft={<>+91</>}
                  width="100%"
                  label="Phone Number"
                  helpText="Used only if you create an event as an organizer."
                />
                <div className='label-14 text-primary-green'>Verified</div>
              </div>) : (
                showInput ? (
                  <>
                  <Input
                    label="Phone Number"
                    contentLeft={<>+91</>}
                    value={phone}
                    onChange={(e) => {
                      setErrors({ ...errors, mobileError: '' });
                      setPhone(e?.target?.value)
                    }}
                    contentRight={<Button type="tertiary-text-underlined" className="!text-primary" onClick={onClickGetOTP}>Get OTP</Button>}
                    required={true}
                    width="100%"
                    helpText="Used only if you create an event as an organizer."
                    {...errors?.mobileError && ({
                      validationStatus: 'error',
                      helpText: errors.mobileError,
                    })}
                    className="form-ph-number-input"
                  />
                  {isOtpOpen && (
                    <Otp
                      formData={{ mobile: phone }}
                      isOpen={isOtpOpen}
                      handlePrimaryClick={handlePrimaryClick}
                      handleSecondaryClick={handleSecondaryClick}
                      onEnterPress={handlePrimaryClick}
                      errors={errors}
                      setErrors={setErrors}
                      onResend={onClickResend}
                    />
                  )}
                  </>
                ) : (
                  <Button
                    type="tertiary"
                    rounded={true}
                    iconLeft={<Plus size={20} weight="bold" />}
                    onClick={() => setShowInput(true)}
                  >
                    Add Phone Number
                  </Button>
                )
              )}
            {(showEditWhatsappNo || whatsappNumber) && (
              <div className="flex flex-col gap-3">
              {showEditWhatsappNo ? (
                <>
                  <div className="flex gap-4 items-start">
                    <Input
                      value={whatsappNumber}
                      contentLeft={<div className='text-black'>+91</div>}
                      width="100%"
                      className="flex-1"
                      onChange={(e) => {
                        setErrors({ ...errors, whatsappError: '' });
                        setWhatsappNumber(e?.target?.value);
                      }}
                      label="Whatsapp number (for free resale)"
                      {...errors?.whatsappError && ({
                        validationStatus: 'error',
                        helpText: errors.whatsappError,
                      })}
                    />
                    <Button type="tertiary-text" className="!text-primary label-16" onClick={onCancel}>Cancel</Button>
                  </div>
                  <Button style={{ width: 185 }} onClick={onClickSave}>Save</Button>
                </>
              ) : (
              <Verified
                heading="Whatsapp number (for free resale)"
                helpText="Used only for your resale listings. Not connected to ticket bookings."
                cta={<Button type="tertiary-text-underlined" onClick={() => setShowEditWhatsappNo(true)}>Edit</Button>}
                content={'+91 ' + whatsappNumber}
              />
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
